'use client'

import { useEffect, useState } from 'react'

import { useRouter } from 'next/navigation'

import Typography from '@mui/material/Typography'

import classnames from 'classnames'

import { signIn, useSession } from 'next-auth/react'

import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'

import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo'

import dayjs from 'dayjs'

import axios from 'axios'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import themeConfig from '@configs/themeConfig'

import CustomButton from '@/components/layout/shared/CustomButton'
import LogoLogin from '@/components/layout/shared/LogoLogin'
import NumericFormatCustom from '@/components/NumericFormatCustom'
import getUrlApiLogin from '@/utils/getUrlApiLogin'
import { useSettings } from '@core/hooks/useSettings'

import showToastAlert from '@components/Toast'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

const dataShift = [
  { value: 0, label: 'Pagi' },
  { value: 1, label: 'Siang' },
  { value: 2, label: 'Malam' }
]

const LoginPage = () => {
  const { settings } = useSettings()

  const router = useRouter()
  const { data: session, status } = useSession()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loading, setLoading] = useState(false)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [open, setOpen] = useState(false)
  const [tglTransOrder, setTglTransOrder] = useState(dayjs().toString())
  const [selectedShift, setselectedShift] = useState('')
  const [saldo, setSaldo] = useState('')
  const [pegawai, setPegawai] = useState('')
  const [idUnit, setIdUnit] = useState('')
  const [idPegawai, setIdPegawai] = useState('')
  const [loadingSimpan, setLoadingSimpan] = useState<boolean>(false)

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    if (status === 'authenticated') {
      const redirectTo = '/kasir-home'

      router.push(redirectTo)
    }
  }, [status, session, router])

  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!username) {
      showToastAlert('info', 'Username tidak boleh kosong')

      return
    }

    if (!password) {
      showToastAlert('info', 'Password tidak boleh kosong')

      return
    }

    setLoading(true)
    await clearAllCookies()

    const url = await getUrlApiLogin()

    try {
      const response = await axios.post(url + 'login/loginadmin', {
        user: username,
        pass: password
      })
      if (response.data.metadata.code === 200) {
        const session = response.data.response.list[0]

        if (session) {
          const idJabatan = session?.ID_JABATAN
          const idPegawai = session?.ID_PEGAWAI
          const idUnit = session?.ID_UNIT

          if (idJabatan === '3' || idJabatan === 3) {
            setPegawai(session?.NAMA_PEGAWAI)
            setIdPegawai(session?.ID_PEGAWAI)
            setIdUnit(session?.ID_UNIT)

            cariRegKasir(idPegawai!, idUnit!)
          } else {
            showToastAlert('info', 'Anda bukan kasir')
          }
        } else {
          showToastAlert('info', 'Username atau password salah')
        }
      } else {
        showToastAlert('info', response.data.metadata.message)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const clearAllCookies = async () => {
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }

  const doLoginSession = async (noreg: string = '', url: string = '') => {
    setLoading(true)
    let isurlpublic = ''

    if (url === (process.env.NEXT_PUBLIC_API_URL as string)) {
      isurlpublic = 'true'
    } else {
      isurlpublic = 'false'
    }

    try {
      const result = await signIn('credentials', {
        redirect: false,
        user: username,
        pass: password,
        noreg: noreg,
        isurlpublic: isurlpublic
      })

      if (result?.error) {
        showToastAlert('info', result.error)
      } else {
        router.push(themeConfig.homePageUrl)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const cariRegKasir = async (idPegawai: string, idUnit: string) => {
    const url = await getUrlApiLogin()

    try {
      const response = await axios.post(url + 'kasir/cariregkasir', {
        idpegawai: idPegawai,
        idunit: idUnit
      })

      if (response.data.metadata.code === 200) {
        const data = response.data.response.list[0]

        doLoginSession(data?.NOMOR, url as string)
      } else {
        setOpen(true)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    }
  }

  const handleClose = () => {
    localStorage.removeItem('sessionExpiry')
    setOpen(false)
  }

  const handleShiftChange = (event: any, value: any) => {
    if (value) {
      setselectedShift(value.value)
    } else {
      setselectedShift('')
    }
  }

  const saveRegistrasi = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setLoadingSimpan(true)
    const urlH = await getUrlApiLogin()

    try {
      const url = `${urlH}kasir/saveregkasir`

      const response = await axios.post(url, {
        nomor: null,
        idunit: idUnit,
        tgl: dayjs(tglTransOrder).format('YYYY-MM-DD HH:mm:ss'),
        idpegawai: idPegawai,
        shift: selectedShift || 0,
        saldo: saldo || '0',
        idaktif: '0'
      })

      if (response.data.metadata.code === 200) {
        setOpen(false)
        const data = response.data.response.list[0]

        doLoginSession(data?.NOMOR, urlH as string)
      } else {
        showToastAlert('info', response.data.metadata.message)
      }
    } catch (error) {
      console.error('Error:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoadingSimpan(false)
    }
  }

  return (
    <>
      <Dialog fullWidth open={open} onClose={handleClose} maxWidth='xs' scroll='body' transitionDuration={0}>
        <form noValidate autoComplete='off' onSubmit={saveRegistrasi} className='flex flex-col gap-5'>
          <DialogContent className='overflow-visible pbs-0 sm:pbe-2 sm:pli-5'>
            <div className='flex justify-between items-center mb-5 mt-5'>
              <Typography variant='h5'>Registrasi Kasir</Typography>
              <IconButton onClick={handleClose} color='primary' size='small'>
                <i className='ri-close-line' />
              </IconButton>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} mt={-1}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DateTimePicker']}>
                    <DemoItem>
                      <DateTimePicker
                        readOnly
                        label='Tanggal'
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f0f0f0'
                          },
                          '& .MuiIconButton-root': {
                            color: '#f0f0f0' // Samakan warna icon dengan background
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            InputProps: {
                              inputProps: {
                                style: {
                                  textTransform: 'uppercase',
                                  backgroundColor: '#f0f0f0',
                                  opacity: 1
                                }
                              },
                              readOnly: true
                            }
                          }
                        }}
                        format='YYYY-MM-DD HH:mm'
                        ampm={false}
                        value={tglTransOrder ? dayjs(tglTransOrder) : dayjs()}
                        onChange={newValue => {
                          setTglTransOrder(newValue?.toString() ?? '')
                        }}
                      />
                    </DemoItem>
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  size='small'
                  label='Pegawai'
                  onChange={e => setPegawai(e.target.value)}
                  defaultValue={pegawai}
                  placeholder='Pegawai'
                  InputProps={{
                    inputProps: {
                      style: {
                        textTransform: 'uppercase',
                        backgroundColor: '#f0f0f0',
                        opacity: 1
                      }
                    },
                    readOnly: true
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  disableClearable
                  size='small'
                  options={dataShift}
                  getOptionLabel={option => option.label}
                  value={
                    dataShift.find(option => option.value === Number(selectedShift)) ?? {
                      label: '',
                      value: 0
                    }
                  }
                  onChange={handleShiftChange}
                  renderInput={params => <TextField {...params} label='Pilih Shift' />}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  size='small'
                  label='Saldo'
                  onChange={e => setSaldo(e.target.value)}
                  defaultValue={saldo}
                  placeholder='Saldo'
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    inputProps: {
                      style: { textTransform: 'uppercase', textAlign: 'right', opacity: 1 }
                    }
                  }}
                  sx={{
                    '& .MuiInput-input': {
                      textAlign: 'right'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className='justify-center pbs-0 sm:pbe-6 sm:pli-5'>
            <CustomButton type='submit' loading={loadingSimpan}>
              Simpan
            </CustomButton>
            <CustomButton variant='outlined' type='reset' onClick={handleClose} color='secondary' loading={false}>
              Keluar
            </CustomButton>
          </DialogActions>
        </form>
      </Dialog>
      {!open && (
        <div className='flex bs-full justify-center'>
          <div
            className={classnames(
              'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
              {
                'border-ie': settings.skin === 'bordered'
              }
            )}
          >
            <img
              src='/images/icon/logo_bg_white.png'
              alt=''
              className='absolute inset-0 w-full h-full object-cover z-[-1]'
            />
          </div>
          <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
            <div className='absolute block sm:hidden block-start-5 inline-start-6'>
              <LogoLogin />
            </div>
            <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
              <div>
                <Typography variant='h5' className='text-center'>
                  Selamat datang di
                  <strong>{` ${themeConfig.templateName}! 👋🏻`}</strong>
                </Typography>
              </div>

              <form noValidate onSubmit={doLogin} autoComplete='off' className='flex flex-col gap-5'>
                <TextField autoFocus fullWidth label='Username' onChange={e => setUsername(e.target.value)} />
                <TextField
                  fullWidth
                  label='Password'
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={e => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          edge='end'
                          onClick={handleClickShowPassword}
                          onMouseDown={e => e.preventDefault()}
                        >
                          <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <CustomButton type='submit' loading={loading} color='primary' fullWidth={true}>
                    Log In
                  </CustomButton>
                </Box>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default LoginPage
